// The one animation hook for the chart library.
//
// It writes to the DOM IMPERATIVELY, through an `apply(el, value)` callback,
// and never sets React state per frame. That is the whole design: a dashboard
// with a dozen live instruments at 60 fps would otherwise commit ~720 React
// renders a second, and the reconciler — not the animation — becomes the cost.
// The component renders when its DATA changes; the needle moves without it.
//
// Two modes. Supplying `at` opts into timeline rendering; within that, the
// mode is then chosen by the OBSERVED gap between samples, so the boat can
// change its feed rate without the browser being retuned (see ADR-104):
//
//   * TIMELINE — buffer timestamped samples and play them back on a fixed lag,
//     interpolating between them. Used when samples arrive closer together
//     than the spring's settle time, where interpolation earns its lag.
//   * SPRING-ON-CHANGE — spring straight at the newest value. Used when `at`
//     is omitted (ChartLab, HelmDemo — unchanged feel, no call-site changes)
//     AND when samples arrive far enough apart that the spring alone already
//     covers the gap continuously.
//
// See `motion.ts` for the math and why it is shaped this way.

import { useCallback, useEffect, useRef } from 'react';

import {
  LAG_MS,
  pushSample,
  reanchor,
  recentGapMs,
  snapSpring,
  springSettled,
  stepSpring,
  TIMELINE_MAX_GAP_MS,
  valueAt,
  type MotionSample,
  type SpringState,
} from './motion';

export interface SmoothOptions {
  /** Treat the value as a bearing: unwind so a needle crossing north sweeps
   *  the short way instead of spinning all the way back round the dial. */
  angular?: boolean;
  /** Skip animation entirely and write each value as it arrives. Independent
   *  of the reduced-motion check below, which is applied on top. */
  instant?: boolean;
}

/** True when the OS asks for reduced motion. Read per-hook rather than cached
 *  so a mid-session preference change takes effect on the next mount. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Smooth `value` and write it to an element via `apply`.
 *
 * Returns a ref CALLBACK — attach it to the element you want driven. A
 * callback (not an object ref) so the hook learns about mount and unmount and
 * can start and stop its loop exactly when the element exists.
 *
 * @param value   the latest reading, or null for "no value" (freeze, don't zero)
 * @param at      the reading's DISPLAY time (ms). Omit for spring-on-change.
 * @param apply   writes the smoothed number to the DOM. Must be stable.
 */
export function useSmoothedNumber<E extends Element>(
  value: number | null,
  at: number | undefined,
  apply: (el: E, smoothed: number) => void,
  opts: SmoothOptions = {}
): (el: E | null) => void {
  const { angular = false, instant = false } = opts;

  const elRef = useRef<E | null>(null);
  const bufferRef = useRef<MotionSample[]>([]);
  const springRef = useRef<SpringState | null>(null);
  const frameRef = useRef<number | null>(null);
  const lastFrameAtRef = useRef<number>(0);
  // Spring-on-change mode's target, kept in a ref so the loop reads the latest
  // without the effect having to restart it.
  const targetRef = useRef<number | null>(null);
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const angularRef = useRef(angular);
  angularRef.current = angular;

  const reduced = prefersReducedMotion();
  const skipAnimation = instant || reduced;

  /** Write a value straight out and park the spring there. */
  const write = useCallback((v: number) => {
    springRef.current = snapSpring(v);
    const el = elRef.current;
    if (el !== null) applyRef.current(el, v);
  }, []);

  const stop = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    frameRef.current = null;
    const el = elRef.current;
    if (el === null) return;

    const now = Date.now();
    const rafNow = performance.now();
    const dt = lastFrameAtRef.current === 0 ? 0 : (rafNow - lastFrameAtRef.current) / 1000;
    lastFrameAtRef.current = rafNow;

    // Mode follows the OBSERVED cadence, not an assumed feed rate. Samples
    // closer together than the spring's settle time are worth interpolating on
    // a lagged playhead; samples further apart are not — the spring already
    // covers that gap continuously, and a playhead a full gap behind would
    // just show older wind. See TIMELINE_MAX_GAP_MS.
    const buffer = bufferRef.current;
    const newest = buffer[buffer.length - 1];
    let target: number | null;
    if (newest === undefined) {
      target = targetRef.current;
    } else {
      const gap = recentGapMs(buffer);
      target =
        gap !== null && gap <= TIMELINE_MAX_GAP_MS ? valueAt(buffer, now - LAG_MS) : newest.value;
    }
    if (target === null) return;

    const spring = springRef.current;
    if (spring === null) {
      // First value: snap. Springing from an implicit 0 would swing the needle
      // up from north on every mount and every recovery from a dropout.
      write(target);
    } else {
      const next = stepSpring(spring, target, dt);
      springRef.current = next;
      applyRef.current(el, next.x);
      // Settled AND no new samples to play: stop. An idle gauge must burn zero
      // frames — a dashboard of instruments each holding a loop open is a
      // battery bug, and the alternative to stopping is never stopping.
      if (
        springSettled(next, target) &&
        now - LAG_MS >= (bufferRef.current.at(-1)?.at ?? -Infinity)
      )
        return;
    }
    frameRef.current = requestAnimationFrame(tick);
  }, [write]);

  const start = useCallback(() => {
    if (frameRef.current !== null || skipAnimation) return;
    lastFrameAtRef.current = 0;
    frameRef.current = requestAnimationFrame(tick);
  }, [skipAnimation, tick]);

  // Feed each new reading in. Idempotent per (value, at), so StrictMode's
  // double-invoke cannot push the same sample twice.
  useEffect(() => {
    if (value === null) {
      // Dropout: freeze at the last real reading rather than falling to zero.
      // The card's staleness dimming carries the honesty; a needle that swings
      // to north claims a reading the instrument never took.
      stop();
      return;
    }
    if (skipAnimation) {
      write(value);
      return;
    }
    if (at === undefined) {
      targetRef.current = value;
      bufferRef.current = [];
      start();
      return;
    }
    bufferRef.current = reanchor(
      pushSample(bufferRef.current, at, value, { angular: angularRef.current, now: Date.now() })
    );
    start();
  }, [value, at, skipAnimation, start, stop, write]);

  // A backgrounded tab stops firing rAF while samples keep arriving, so the
  // buffer fills with history the playhead would then crawl through in slow
  // motion. Drop it and snap once — the instrument shows now, not a replay.
  //
  // The loop must be torn down and rebuilt, not just left to resume. While
  // hidden, `frameRef` holds a request that will never fire, so every `start()`
  // in between saw a loop "already running" and no-opped. Cancelling it here is
  // what lets the restart actually take.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      stop();
      const latest = bufferRef.current.at(-1);
      bufferRef.current = latest === undefined ? [] : [latest];
      if (latest !== undefined) write(latest.value);
      else if (targetRef.current !== null) write(targetRef.current);
      start();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [write, start, stop]);

  useEffect(() => stop, [stop]);

  return useCallback(
    (el: E | null) => {
      elRef.current = el;
      if (el === null) {
        stop();
        return;
      }
      // Paint the current value immediately on mount so the element is never
      // shown at a stale or default position for a frame.
      const spring = springRef.current;
      if (spring !== null) applyRef.current(el, spring.x);
      start();
    },
    [start, stop]
  );
}

/**
 * An applier that rotates an SVG group about a fixed origin.
 *
 * Cached per origin so repeated calls with the same center return the SAME
 * function. The hook holds `apply` in a ref, so a fresh identity would not
 * break anything — but a stable one keeps it honest as a plain value and out
 * of every dependency array.
 */
export function applyDashOffset(el: SVGPathElement, offset: number): void {
  el.setAttribute('stroke-dashoffset', String(offset));
}

const rotateAppliers = new Map<number, (el: SVGGElement, deg: number) => void>();
export function applyRotate(origin: number): (el: SVGGElement, deg: number) => void {
  const cached = rotateAppliers.get(origin);
  if (cached !== undefined) return cached;
  const fn = (el: SVGGElement, deg: number): void => {
    el.style.transformOrigin = `${origin}px ${origin}px`;
    el.style.transform = `rotate(${deg}deg)`;
  };
  rotateAppliers.set(origin, fn);
  return fn;
}
